import * as DateF from 'date-fns'
import { match } from 'ts-pattern'

import { Duration } from '@blade/std-lib'
import { DurationFormatter } from '@blade/ui-kit'
import { useDateFnsLocale, useTranslation } from '@root/shared/localization'

import type { StrictTransFn } from '@root/shared/localization'

type FormatDurationMode =
  | 'singleUnit'
  | 'singleUnitShort'
  | 'units'
  | 'unitsShort'

type FormatDurationArgs = {
  startAt?: Date | undefined
  endAt?: Date | undefined
  withAgo?: boolean
  units?: Array<DateF.DurationUnit>
  mode?: FormatDurationMode
}

export const useFormatDuration = () => {
  const { t } = useTranslation()
  const locale = useDateFnsLocale()

  /**
   * @description Format duration between two dates for LTR locales, RTL is not supported
   * @param args - Configuration object for formatting duration
   * @param args.startAt - Start date to calculate duration from. Defaults to current date if not provided
   * @param args.endAt - End date to calculate duration to. Defaults to current date if not provided
   * @param args.withAgo - If true, appends "ago" to the formatted duration. Defaults to false
   * @param args.units - Array of duration units to include in formatting. Defaults to ['years',...,'minutes']
   * @param args.mode - Formatting mode:
   *  - 'singleUnit' (e.g. "5 hours")
   *  - 'singleUnitShort' (e.g. "5h")
   *  - 'units' (e.g. "5 hours 30 minutes")
   *  - 'unitsShort' (e.g. "5h 30m")
   */
  return {
    formatDuration: (
      args: FormatDurationArgs = {
        startAt: undefined,
        endAt: undefined,
        withAgo: false,
        units: ['years', 'months', 'weeks', 'days', 'hours', 'minutes'],
        mode: 'units',
      },
    ): string | undefined => {
      const { units, mode, withAgo } = args
      const startAt = args.startAt ?? new Date()
      const endAt = args.endAt ?? new Date()
      const intervalMs = endAt.getTime() - startAt.getTime()

      if (intervalMs <= 999) {
        const time0sec = t('common.format.seconds_short', { count: 0 })

        return withAgo ? t('common.ago', { time: time0sec }) : time0sec
      }

      const time = match(mode)
        .with('units', () => formatUnits(startAt, endAt, locale, units))
        .with('unitsShort', () => formatUnitShort(startAt, endAt, t, units))
        .with('singleUnit', () => formatSingleUnit(startAt, endAt, locale))
        .with('singleUnitShort', () => formatSingleUnitShort(startAt, endAt, t))
        .otherwise(() => undefined)

      return withAgo ? t('common.ago', { time }) : time
    },
  }
}

const formatUnits = (
  startAt: Date,
  endAt: Date,
  locale: DateF.Locale,
  units?: Array<DateF.DurationUnit>,
) => {
  const duration = DateF.intervalToDuration({
    start: startAt,
    end: endAt,
  })

  return DateF.formatDuration(duration, {
    format: units,
    locale,
  })
}

const formatSingleUnit = (startAt: Date, endAt: Date, locale: DateF.Locale) => {
  const duration = DateF.intervalToDuration({
    start: startAt,
    end: endAt,
  })

  return DurationFormatter.formatDurationSingleUnit(duration, {
    locale,
  })
}

const formatSingleUnitShort = (
  startAt: Date,
  endAt: Date,
  t: StrictTransFn,
) => {
  const duration = DateF.intervalToDuration({
    start: startAt,
    end: endAt,
  })

  return DurationFormatter.formatDurationSingleUnit(duration, {
    locale: {
      formatDistance: (unit, count) =>
        match(unit)
          .with('xYears', () => t('common.format.years_short', { count }))
          .with('xMonths', () => t('common.format.months_short', { count }))
          .with('xWeeks', () => t('common.format.weeks_short', { count }))
          .with('xDays', () => t('common.format.days_short', { count }))
          .with('xHours', () => t('common.format.hours_short', { count }))
          .with('xMinutes', () => t('common.format.minutes_short', { count }))
          .with('xSeconds', () => t('common.format.seconds_short', { count }))
          .otherwise(() => ''),
    },
  })
}

const formatUnitShort = (
  startAt: Date,
  endAt: Date,
  t: StrictTransFn,
  units?: Array<DateF.DurationUnit>,
) => {
  const intervalMs = endAt.getTime() - startAt.getTime()
  const intervalSec = Duration.millis(intervalMs)

  return DurationFormatter.formatDurationShort(
    intervalSec,
    {
      seconds: t('common.format.seconds_short'),
      minutes: t('common.format.minutes_short'),
      hours: t('common.format.hours_short'),
      days: t('common.format.days_short'),
      weeks: t('common.format.weeks_short'),
      months: t('common.format.months_short'),
      years: t('common.format.years_short'),
    },
    units,
  )
}

export const formatDurationShortLocalized = (
  duration: Duration.Duration,
  t: StrictTransFn,
  units?: Array<DateF.DurationUnit>,
) => {
  return DurationFormatter.formatDurationShort(
    duration,
    {
      seconds: t('common.format.seconds_short'),
      minutes: t('common.format.minutes_short'),
      hours: t('common.format.hours_short'),
      days: t('common.format.days_short'),
      weeks: t('common.format.weeks_short'),
      months: t('common.format.months_short'),
      years: t('common.format.years_short'),
    },
    units,
  )
}
